import * as THREE from 'three';
import { PrismParticle, DataPacket } from './three-particles';

export class AnimationLoop {
  constructor({
    composer, filmPass, params, onStatsChange,
    coreGroup, particleGroup, streamGroup,
    liquidGlassMat, innerLight, prismMesh, originalPositions,
    textures, liquidNormalMap, controls, starMat, shootingStars, glowOrbs
  }) {
    this.composer = composer;
    this.filmPass = filmPass;
    this.controls = controls;
    this.starMat = starMat;
    this.shootingStars = shootingStars;
    this.glowOrbs = glowOrbs;
    this.params = params;
    this.onStatsChange = onStatsChange;
    this.coreGroup = coreGroup;
    this.particleGroup = particleGroup;
    this.streamGroup = streamGroup;
    this.liquidGlassMat = liquidGlassMat;
    this.innerLight = innerLight;
    this.prismMesh = prismMesh;
    this.originalPositions = originalPositions;
    this.textures = textures;
    this.liquidNormalMap = liquidNormalMap;

    this.clock = new THREE.Clock();
    this.systemEvolution = 0.3;
    this.pulseTimer = 0.28;
    this.MAX_PARTICLES = 2000;
    this.particles = [];
    this.packetPool = [];
    this.streams = [];
    this.lines = null;
    this.animationFrameId = null;

    this.init();
  }

  init() {
    for (let i = 0; i < 150; i++) {
      this.packetPool.push(new DataPacket(this.particleGroup, this.textures.texDot, this.params.packetColor));
    }

    for (let i = 0; i < 30; i++) {
      const x = (i - 15) * 2.0;
      const s = {
        sprite: new THREE.Sprite(new THREE.SpriteMaterial({ map: this.textures.texOne, color: this.params.streamColor, transparent: true, opacity: 0 })),
        speed: 0.02 + Math.random() * 0.04,
        y: Math.random() * 20
      };
      s.sprite.position.set(x, s.y, 0);
      s.sprite.scale.set(0.3, 0.3, 0.3);
      this.streamGroup.add(s.sprite);
      this.streams.push(s);
    }

    const MAX_LINES = 5000;
    this.lineMat = new THREE.LineBasicMaterial({ color: this.params.outerNetColor, transparent: true, opacity: this.params.outerNetOpacity, blending: THREE.AdditiveBlending });
    const lineGeo = new THREE.BufferGeometry();
    const linePos = new Float32Array(MAX_LINES * 6);
    lineGeo.setAttribute("position", new THREE.BufferAttribute(linePos, 3));
    this.lines = new THREE.LineSegments(lineGeo, this.lineMat);
    this.lines.renderOrder = 1;
    this.particleGroup.add(this.lines);
  }

  spawnDataPacket(p1, p2) {
    const packet = this.packetPool.find((p) => !p.active);
    if (packet) packet.spawn(p1, p2);
  }

  start() {
    this.animate();
  }

  stop() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  }

  animate() {
    this.animationFrameId = requestAnimationFrame(() => this.animate());
    const delta = this.clock.getDelta();
    const time = this.clock.getElapsedTime();

    if (this.systemEvolution < 1.0) this.systemEvolution += 0.008 * this.params.evolutionSpeed;

    const pct = Math.floor(this.systemEvolution * 100);
    const structurePct = Math.floor((this.particles.length / this.MAX_PARTICLES) * 100);
    const activePackets = this.packetPool.filter((p) => p.active).length;

    let status = "INITIALIZING";
    if (pct > 20) status = "LIQUID CORE STABLE";
    if (pct > 60) status = "NETWORK SYNCING";
    if (structurePct >= 95) status = "SYSTEM OPTIMAL";

    if (this.onStatsChange) {
      this.onStatsChange({ status, integrity: pct, capacity: structurePct, nodes: this.particles.length, dataFlow: activePackets });
    }

    this.streams.forEach((s) => (s.sprite.material.opacity = Math.max(0, this.systemEvolution - 0.1) * 0.2));

    const wobbleOn = this.params.wobbleEnabled !== false && this.params.wobbleStrength > 0;
    if (this.prismMesh) {
      const positions = this.prismMesh.geometry.attributes.position;
      if (wobbleOn) {
        for (let i = 0; i < positions.count; i++) {
          const orig = this.originalPositions[i];
          const waveX = Math.sin(time * this.params.wobbleSpeed + orig.y * 0.5) * this.params.wobbleStrength;
          const waveY = Math.cos(time * this.params.wobbleSpeed + orig.x * 0.5) * this.params.wobbleStrength;
          const waveZ = Math.sin(time * this.params.wobbleSpeed * 0.8 + orig.z * 0.5) * this.params.wobbleStrength;
          positions.setXYZ(i, orig.x + waveX, orig.y + waveY, orig.z + waveZ);
        }
        positions.needsUpdate = true;
      } else if (this.wobbleWasOn) {
        // Wobble kapanınca şekli orijinaline döndür
        for (let i = 0; i < positions.count; i++) {
          const orig = this.originalPositions[i];
          positions.setXYZ(i, orig.x, orig.y, orig.z);
        }
        positions.needsUpdate = true;
      }
      this.wobbleWasOn = wobbleOn;
    }

    this.pulseTimer += delta;
    const glowMode = this.params.coreGlowMode || 'pulse';
    if (this.pulseTimer >= 0.3) {
      this.pulseTimer = 0;
      if (glowMode === 'pulse') {
        this.innerLight.intensity = this.params.lightIntensity * 1.5;
        this.liquidGlassMat.emissiveIntensity = this.params.emissiveIntensity * 2.0;
      }
      // Parçacık üretimi her modda devam eder
      if (this.particles.length < this.MAX_PARTICLES) {
        const baseCount = 100 + Math.floor(this.systemEvolution * 200);
        const extraBoost = this.particles.length < 1000 ? 200 : 0;
        const count = baseCount + extraBoost;
        for (let i = 0; i < count; i++) {
          if (this.particles.length < this.MAX_PARTICLES) this.particles.push(new PrismParticle(this.particleGroup, this.textures.texZero, this.textures.texOne, this.params.particleColor));
        }
      }
    } else if (glowMode === 'pulse') {
      this.innerLight.intensity = THREE.MathUtils.lerp(this.innerLight.intensity, this.params.lightIntensity, 0.1);
      this.liquidGlassMat.emissiveIntensity = THREE.MathUtils.lerp(this.liquidGlassMat.emissiveIntensity, this.params.emissiveIntensity, 0.1);
    }
    if (glowMode === 'steady') {
      // Sabit (config değerinde)
      this.innerLight.intensity = this.params.lightIntensity;
      this.liquidGlassMat.emissiveIntensity = this.params.emissiveIntensity;
    } else if (glowMode === 'bright') {
      // Sürekli yanık (en parlak seviyede)
      this.innerLight.intensity = this.params.lightIntensity * 1.5;
      this.liquidGlassMat.emissiveIntensity = this.params.emissiveIntensity * 2.0;
    }

    if (this.params.rotationEnabled !== false) {
      this.coreGroup.rotation.x = Math.sin(time * this.params.rotSpeedX) * 0.5;
      this.coreGroup.rotation.y = time * this.params.rotSpeedY;
      this.coreGroup.rotation.z = Math.cos(time * this.params.rotSpeedZ) * 0.3;

      this.particleGroup.rotation.z += 0.003;
      this.particleGroup.rotation.x = Math.sin(time * 0.15) * 0.4;
      this.particleGroup.rotation.y = Math.cos(time * 0.1) * 0.3;
    }

    this.liquidNormalMap.offset.x += 0.001;
    this.liquidNormalMap.offset.y += 0.002;

    this.particles.forEach((p) => p.update(delta));

    this.lineMat.color.set(this.params.outerNetColor);
    this.lineMat.opacity = this.params.outerNetOpacity;
    this.streams.forEach(s => s.sprite.material.color.set(this.params.streamColor));
    this.particles.forEach(p => p.mesh.material.color.set(this.params.particleColor));
    this.packetPool.forEach(p => p.mesh.material.color.set(this.params.packetColor));

    let lineIdx = 0;
    if (this.particles.length > 30) {
      const connectDist = 1.8;
      const lockedParticles = this.particles.filter((p) => p.state === "locked");
      const linePos = this.lines.geometry.attributes.position.array;
      for (let i = 0; i < lockedParticles.length; i++) {
        let connectionsFound = 0;
        for (let j = i + 1; j < lockedParticles.length; j++) {
          const p1 = lockedParticles[i].mesh.position;
          const p2 = lockedParticles[j].mesh.position;
          const dSq = p1.distanceToSquared(p2);
          if (dSq < connectDist * connectDist) {
            if (lineIdx < linePos.length - 6) {
              linePos[lineIdx++] = p1.x; linePos[lineIdx++] = p1.y; linePos[lineIdx++] = p1.z;
              linePos[lineIdx++] = p2.x; linePos[lineIdx++] = p2.y; linePos[lineIdx++] = p2.z;
              if (this.systemEvolution > 0.4 && Math.random() < 0.002) this.spawnDataPacket(p1, p2);
              connectionsFound++;
            }
          }
          if (connectionsFound >= 3) break;
        }
      }
    }
    this.lines.geometry.setDrawRange(0, lineIdx / 3);
    this.lines.geometry.attributes.position.needsUpdate = true;

    this.packetPool.forEach((p) => p.update(delta));
    this.streams.forEach((s) => { s.sprite.position.y -= s.speed; if (s.sprite.position.y < -10) s.sprite.position.y = 10; });

    // Camera controls — apply live params + advance damping / auto-rotate
    if (this.controls) {
      const locked = !!this.params.cameraLocked;
      this.controls.enableRotate = !locked;
      this.controls.autoRotate = locked ? false : !!this.params.cameraAutoRotate;
      this.controls.autoRotateSpeed = this.params.cameraAutoRotateSpeed ?? 1.0;
      this.controls.enableZoom = this.params.cameraEnableZoom !== false;
      this.controls.enablePan = !!this.params.cameraEnablePan;
      this.controls.enableDamping = this.params.cameraDamping !== false;
      this.controls.update();
    }

    if (this.starMat && this.starMat.uniforms && this.starMat.uniforms.uTime) this.starMat.uniforms.uTime.value = time;

    if (this.shootingStars) {
      this.shootingStars.enabled = this.params.shootingStarsEnabled !== false;
      this.shootingStars.frequency = this.params.shootingStarFrequency ?? 1;
      this.shootingStars.setColor(this.params.bgPointsColor);
      this.shootingStars.update(delta);
    }

    if (this.glowOrbs) this.glowOrbs.update(delta, this.params);

    if (this.filmPass) this.filmPass.uniforms.time.value = time;
    this.composer.render();
  }
}
